import React, { Fragment, useMemo } from 'react';
import { Form, Input, Select } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import AccountPrefillBanner from '../components/AccountPrefillBanner';
import PhoneInput from '../components/PhoneInput';
import COUNTRY from 'country-json/src/country-by-abbreviation.json';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { FormFieldPath, requireEtatCountries, useFormPath } from './index';

export default (formPlugin, namespace) => {

    return {
        Renderer: (props) => {

            const identity = formPlugin.formData.identity

            const { listFieldProps, path, absolutePath, storePath } = useFormPath()

            // Les sous-champs du bloc identité sont scopés sous "identity" (comme les
            // champs propres : name=["identity",...]). On étend les DEUX chemins :
            // path → names Antd ; absolutePath → navigation formValues (conditions).
            const childContext = useMemo(() => ({
                path: [...path, "identity"],
                absolutePath: [...absolutePath, "identity"],
                storePath: [...storePath, "identity"],
                listFieldProps: null,
            }), [path, absolutePath, storePath])

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }
            const { data } = props
            const showField = field => !Array.isArray(data.fields) || data.fields.includes(field)

            if (!identity || identity === "NONE") {
                if (props.remove) {
                    props.remove()
                }
                return null
            }

            const isManual = formPlugin.formData?.manual || false
            const reducedWidth = data.reduced ? "48%" : "100%"

            // Compte titulaire connecté : on verrouille l'identité CIVILE (civilité,
            // nom, prénom, email, tél). L'adresse (mode FULL) reste éditable. Piloté par
            // FormEditorRender via formPlugin.setLockIdentity (web uniquement ; en admin
            // ou éditeur, lockIdentity reste falsy → aucun champ verrouillé).
            const lockCivil = typeof formPlugin.isIdentityLocked === "function" && formPlugin.isIdentityLocked()

            // Contrôle « utiliser mon compte » (alerte à deux états + bascule). Inerte en
            // admin (getAccountControl renvoie hasAccount:false).
            const accountControl = typeof formPlugin.getAccountControl === "function"
                ? formPlugin.getAccountControl()
                : {hasAccount: false, disabled: false}

            return (
                <div>

                    {lockCivil && (
                        <AccountPrefillBanner
                            mode="active"
                            title={accountControl.name ? (t`Connecté en tant que` + " " + accountControl.name) : t`Vous êtes connecté à votre compte`}
                            subtitle={t`Vos informations sont pré-remplies`}
                            actionLabel={accountControl.onDisable ? t`Ce n'est pas moi` : null}
                            onAction={accountControl.onDisable || null}
                        />
                    )}

                    {accountControl.hasAccount && accountControl.disabled && (
                        <AccountPrefillBanner
                            mode="disabled"
                            title={t`Vous saisissez d'autres informations`}
                            subtitle={t`Cette soumission ne sera pas rattachée à votre compte`}
                            actionLabel={accountControl.onEnable ? t`Réutiliser mon compte` : null}
                            onAction={accountControl.onEnable || null}
                        />
                    )}

                    {!Array.isArray(data.fields) && <Form.Item noStyle shouldUpdate={true}>
                        {(_form) => {

                            var fixedGenre = false
                            if (data.fixedGenre === "Homme" || data.fixedGenre === "Femme") {
                                fixedGenre = true
                                let fixedValue = data.fixedGenre === "Homme"
                                if (_form.getFieldValue(["identity", "homme"]) !== fixedValue) {
                                    _form.setFieldValue(["identity", "homme"], fixedValue)
                                }
                            }

                            return (
                                <Form.Item
                                    {...(listFieldProps || {})}
                                    label={t`Civilité` + " :"}
                                    name={["identity", "homme"]}
                                    rules={[{
                                        required: !isManual,
                                        message: t`Ce champ est obligatoire`
                                    }]}
                                >
                                    <Select disabled={fixedGenre || lockCivil} size="large" placeholder={t`Civilité`}>
                                        <Select.Option value={true}><Trans>Monsieur</Trans></Select.Option>
                                        <Select.Option value={false}><Trans>Madame</Trans></Select.Option>
                                    </Select>
                                </Form.Item>
                            )
                        }}
                    </Form.Item>}

                    <div style={{ display: data.reduced ? "flex" : "block", justifyContent: "space-between" }}>

                        {showField('firstName') && <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelPrenom || t`Prénom`) + " :"}
                            name={["identity", "prenom"]}
                            rules={[{
                                required: !isManual,
                                message: t`Ce champ est obligatoire`
                            }]}
                            style={{ width: showField('firstName') && showField('lastName') ? reducedWidth : "100%" }}
                        >
                            <Input type="text" placeholder={data.labelPrenom || t`Prénom`} autoComplete="given-name" disabled={lockCivil} />
                        </Form.Item>}

                        {showField('lastName') && <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelNom || t`Nom`) + " :"}
                            name={["identity", "nom"]}
                            rules={[{
                                required: !isManual,
                                message: t`Ce champ est obligatoire`
                            }]}
                            style={{ width: showField('firstName') && showField('lastName') ? reducedWidth : "100%" }}
                        >
                            <Input type="text" placeholder={data.labelNom || t`Nom`} autoComplete="family-name" disabled={lockCivil} />
                        </Form.Item>}

                    </div>

                    <div style={{
                        display: (data.reduced && props.device === "DESKTOP") ? "flex" : "block",
                        justifyContent: "space-between"
                    }}>
                        {showField('email') && <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelEmail || t`Email`) + " :"}
                            name={["identity", "email"]}
                            rules={[{
                                required: !isManual,
                                message: t`Ce champ est obligatoire`
                            }, {
                                pattern: new RegExp(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/),
                                message: t`L'adresse email n'est pas valide`
                            }]}
                            style={{ width: (data.reduced && props.device === "DESKTOP" && showField('email') && showField('phone')) ? reducedWidth : "100%" }}
                        >
                            <Input type="email" placeholder={data.labelEmail || t`Email`} autoComplete="email" disabled={lockCivil} />
                        </Form.Item>}

                        {showField('phone') && <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelTel || t`Téléphone`) + " :"}
                            name={["identity", "tel"]}
                            rules={[{
                                required: !isManual,
                                message: t`Ce champ est obligatoire`
                            }, {
                                validator: (_, _value) => {
                                    if (!_value) return Promise.resolve()
                                    const cleaned = String(_value).replace(/[\s\-().]/g, "")
                                    return /^\+?[0-9]{6,20}$/.test(cleaned)
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(t`Le numéro de téléphone est invalide`))
                                }
                            }]}
                            style={{ width: (data.reduced && props.device === "DESKTOP" && showField('email') && showField('phone')) ? reducedWidth : "100%" }}
                        >
                            <PhoneInput defaultLang={props.lang} placeholder={data.labelTel || t`Téléphone`} disabled={lockCivil} />
                        </Form.Item>}
                    </div>

                    {formPlugin.formData.identity === "FULL" && (
                        <Fragment>
                            <Form.Item
                                {...(listFieldProps || {})}
                                label={(data.labelAdresse || t`Adresse`) + " :"}
                                name={["identity", "adresse"]}
                                rules={[{
                                    required: !isManual,
                                    message: t`Ce champ est obligatoire`
                                }]}
                            >
                                <Input type="text" placeholder={data.labelAdresse || t`Adresse`} autoComplete="address-line1" />
                            </Form.Item>

                            <div style={{ display: data.reduced ? "flex" : "block", justifyContent: "space-between" }}>

                                <Form.Item shouldUpdate noStyle>
                                    {(_form)=>
                                    {

                                        let pays = _form.getFieldValue(["identity", "pays"])
                                        if (!pays) {
                                            pays= COUNTRY.find(i => i.abbreviation === formPlugin.getCountry())?.country
                                        }

                                        let paysCode=COUNTRY.find(i => i.country === pays)?.abbreviation || formPlugin.getCountry()

                                        if("IL"===paysCode){
                                            return null
                                        }


                                        return(
                                            <Form.Item
                                                {...(listFieldProps || {})}
                                                label={(data.labelPostal || t`Code Postal`) + " :"}
                                                name={["identity", "postal"]}
                                                rules={[{
                                                    required: !isManual,
                                                    message: t`Ce champ est obligatoire`
                                                }]}
                                                style={{ width: showField('firstName') && showField('lastName') ? reducedWidth : "100%" }}
                                            >
                                                <Input type="text" placeholder={data.labelPostal || t`Code Postal`} autoComplete="postal-code" />
                                            </Form.Item>
                                        )
                                    }}
                                </Form.Item>


                                <Form.Item
                                    {...(listFieldProps || {})}
                                    label={(data.labelVille || t`Ville`) + " :"}
                                    name={["identity", "ville"]}
                                    rules={[{
                                        required: !isManual,
                                        message: t`Ce champ est obligatoire`
                                    }]}
                                    style={{ width: showField('firstName') && showField('lastName') ? reducedWidth : "100%" }}
                                >
                                    <Input type="text" placeholder={data.labelVille || t`Ville`} autoComplete="home city" />
                                </Form.Item>
                            </div>


                            <Form.Item noStyle shouldUpdate={true}>
                                {(_form) => {

                                    let pays = _form.getFieldValue(["identity", "pays"])
                                    if (!pays) {
                                        pays= COUNTRY.find(i => i.abbreviation === formPlugin.getCountry())?.country
                                    }

                                    let paysCode=COUNTRY.find(i => i.country === pays)?.abbreviation || formPlugin.getCountry()

                                    if(!requireEtatCountries.includes(paysCode)){
                                        return null
                                    }

                                    return (
                                        <Form.Item
                                            {...(listFieldProps || {})}
                                            label={t`État / Province` + " :"}
                                            name={["identity", "etat"]}
                                            rules={[{
                                                required: !isManual,
                                                message: t`Ce champ est obligatoire`
                                            }]}
                                        >
                                            <Input type="text" placeholder={data.labelEtat || t`État / Province`} autoComplete="state" />
                                        </Form.Item>
                                    )
                                }}
                            </Form.Item>


                            <Form.Item noStyle shouldUpdate={true}>
                                {(_form) => {

                                    let pays = _form.getFieldValue(["identity", "pays"])
                                    if (!pays) {
                                        _form.setFieldValue(["identity", "pays"], COUNTRY.find(i => i.abbreviation === formPlugin.getCountry())?.country)
                                    }

                                    return (
                                        <Form.Item
                                            {...(listFieldProps || {})}
                                            label={t`Pays` + " :"}
                                            name={["identity", "pays"]}
                                            rules={[{
                                                required: !isManual,
                                                message: t`Ce champ est obligatoire`
                                            }]}
                                        >
                                            <select className={"form-control"}>
                                                <option unselectable disabled><Trans>Pays</Trans></option>
                                                {COUNTRY.map(i => {
                                                    return (
                                                        <option value={i.country}>{i.country}</option>
                                                    )
                                                })}
                                            </select>
                                        </Form.Item>
                                    )
                                }}
                            </Form.Item>

                            {data.showNote && (
                                <Form.Item
                                    {...(listFieldProps || {})}
                                    label={(data.labelNote || t`Complément d'adresse`) + " :"}
                                    name={["identity", "note_adresse"]}
                                >
                                    <Input placeholder={data.labelNote || t`Complément d'adresse`} autoComplete="address-line2" />
                                </Form.Item>
                            )}
                        </Fragment>
                    )}

                    <div className="form-field" data-field={JSON.stringify({ key: "identity", name: "identity" })}>
                        <FormFieldPath.Provider value={childContext}>
                            {props.children}
                        </FormFieldPath.Provider>
                    </div>

                </div>
            )
        },
        namespace: namespace,
        id: 'form-identity' + namespace,
        title: t`Block Identitaire`,
        description: t`Élément de formulaire utilisable.`,
        icon: <UserOutlined />,
        iconColor: "#138D75",
        version: 1,
        cellPlugins: (plugins, data) => {
            const initialPlugins = formPlugin.getPluginsMem(namespace).filter(i => !i.id.includes("form-contact") && !i.id.includes("form-child") && !i.id.includes("form-hazkara") && !i.id.includes("form-identity") && !i.id.includes("form-product") && !i.id.includes("form-step-container") && !i.id.includes("form-submit-button") && !i.id.includes("form-free-amount") && !i.id.includes("form-paiement-method") && !i.id.includes("form-calendar"))
            return [...initialPlugins, ...formPlugin.getContactFormPlugins(namespace)]
        },
        hideInMenu: (!formPlugin.formData.identity || formPlugin.formData.identity === "NONE"),
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    reduced: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Formulaire Réduit`,
                        }
                    },
                    labelNom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Nom`,
                            placeholder: t`Étiquette Nom`,
                        }
                    },
                    labelPrenom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Prénom`,
                            placeholder: t`Étiquette Prénom`,
                        }
                    },
                    labelEmail: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Email`,
                            placeholder: t`Étiquette Email`,
                        }
                    },
                    labelTel: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Téléphone`,
                            placeholder: t`Étiquette Téléphone`,
                        }
                    },
                    labelAdresse: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Adresse`,
                            placeholder: t`Étiquette Adresse`,
                            showIf: () => formPlugin.formData.identity === "FULL"
                        }
                    },
                    labelPostal: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Postal`,
                            placeholder: t`Étiquette Postal`,
                            showIf: () => formPlugin.formData.identity === "FULL"
                        }
                    },
                    labelVille: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Ville`,
                            placeholder: t`Étiquette Ville`,
                            showIf: () => formPlugin.formData.identity === "FULL"
                        }
                    },
                    showNote: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Afficher le Complément d'adresse`,
                        }
                    },
                    fixedGenre: {
                        type: "string",
                        uniforms: {
                            label: t`Fixé le genre`,
                            allowedValues: ["Choisir", "Homme", "Femme"],
                        },
                    },
                }
            },
        },
    }
}
